import { endOfDay, sub } from 'date-fns';
import _ from 'lodash';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useProjects } from '@/hooks/use-projects';
import { AreaChart } from '@logspot-platform/widgets';
import { DailyActiveUsersService, EventsService } from '@/api';
import type { DateRange, EventData, EventDataMap, Project } from '@/types';
import { createFileRoute, Link } from '@tanstack/react-router';
import { LoadError } from '@/components/dashboard/load-error';
import { OnlineNowBadge } from '@/components/dashboard/online-now-badge';
import { OverviewCard } from '@/components/dashboard/overview-card';
import { SectionHeading } from '@/components/dashboard/section-heading';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatures } from '@/hooks/use-features';
import {
  allEventsOptions,
  allEventsPrevOptions,
  dashboardDauOptions,
  featuresOptions,
  projectsOptions,
} from '@/hooks/query-options';

const now = new Date();

const dateRange: DateRange = {
  interval: '1 days',
  startDate: sub(now, { days: 30 }),
  endDate: endOfDay(now),
  period: { days: 30 },
};

export const Route = createFileRoute('/_auth/_side-layout/home')({
  component: Home,
  // Prefetch each project's DAU + event-count series in parallel on intent.
  // Without this, every ProjectBox fetches on render via two sequential
  // useSuspenseQuery calls (a per-card waterfall) and the page stalls.
  loader: async ({ context }) => {
    const [projects, features] = await Promise.all([
      context.queryClient.ensureQueryData(projectsOptions()),
      context.queryClient.ensureQueryData(featuresOptions()),
    ]);
    if (!Array.isArray(projects)) return;
    for (const project of projects as Project[]) {
      const projectId = project.projectId;
      if (features.dashboardV2 === true) {
        // Event counts refetch on mount anyway, so only users is worth prefetching.
        context.queryClient.prefetchQuery(
          dashboardDauOptions({ projectId, dateRange, filterGroups: [] }),
        );
        continue;
      }
      context.queryClient.prefetchQuery({
        queryKey: ['master-dau', projectId, JSON.stringify(dateRange)],
        queryFn: () =>
          DailyActiveUsersService.getDau({
            projectId,
            interval: dateRange.interval,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }),
      });
      context.queryClient.prefetchQuery({
        queryKey: ['master-count', projectId, JSON.stringify(dateRange)],
        queryFn: () =>
          EventsService.getAllEventCounts({
            projectId,
            interval: dateRange.interval,
            startDate: dateRange.startDate.getTime(),
            endDate: dateRange.endDate.getTime(),
          }),
      });
    }
  },
});

type ProjectBoxProps = {
  project: Project;
  setProjectId: (projectId: string) => void;
};

// const ProjectBox = ({ project, now, setProject }) => {
const ProjectBox = ({ project, setProjectId }: ProjectBoxProps) => {
  const projectId = project.projectId;
  const { data: users } = useSuspenseQuery({
    queryKey: [`master-dau`, projectId, JSON.stringify(dateRange)],
    queryFn: () =>
      DailyActiveUsersService.getDau({
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
  });

  const { data: events } = useSuspenseQuery<EventDataMap>({
    queryKey: ['master-count', project.projectId, JSON.stringify(dateRange)],
    queryFn: () =>
      EventsService.getAllEventCounts({
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate.getTime(),
        endDate: dateRange.endDate.getTime(),
      }),
  });

  const summedEvents: EventData[] = Object.values(events)
    .flat()
    .reduce((acc: EventData[], event: EventData) => {
      const existingEventIndex = acc.findIndex((e) => e.date === event.date);
      if (existingEventIndex === -1) {
        acc.push({
          name: 'event',
          date: event.date,
          count: parseInt(event.count.toString()),
        });
      } else {
        const existingEvent = acc[existingEventIndex];
        if (existingEvent) {
          existingEvent.count =
            Number(existingEvent.count) + parseInt(event.count.toString());
        }
      }
      return acc;
    }, []);

  return (
    <div className="w-full">
      <div className="rounded border">
        <div className="border-border border-b p-5">
          <Link to="/">
            <Button
              className="text-foreground text-xl font-bold"
              variant="link"
              onClick={() => {
                setProjectId(project.projectId);
              }}
            >
              {project.name}
            </Button>
          </Link>
        </div>
        <div className="p-5">
          <div className="flex flex-col gap-2">
            <Label>Users</Label>
            <AreaChart data={users?.perDay ?? []} dateRange={dateRange} />
            <Separator />
            <Label>Events</Label>
            <AreaChart data={summedEvents} dateRange={dateRange} />
          </div>
        </div>
      </div>
    </div>
  );
};

// The redesigned dashboard's overview, one per project.
function ProjectOverview({
  project,
  onOpen,
}: {
  project: Project;
  onOpen: () => void;
}) {
  const range = { projectId: project.projectId, dateRange, filterGroups: [] };
  const events = useQuery(allEventsOptions(range));
  const prevEvents = useQuery(allEventsPrevOptions(range));
  const idle =
    Object.keys(events.data ?? {}).length === 0 &&
    Object.keys(prevEvents.data ?? {}).length === 0;

  return (
    <div className="flex flex-col gap-3">
      <SectionHeading
        action={
          <span className="flex items-center gap-3">
            {!idle && (
              <OnlineNowBadge
                dateRange={dateRange}
                filterGroups={[]}
                projectId={project.projectId}
              />
            )}
            <Button asChild size="sm" variant="link">
              <Link onClick={onOpen} to="/">
                Open Dashboard
              </Link>
            </Button>
          </span>
        }
        eyebrow="Project"
        id={`project-${project.projectId}`}
        title={project.name}
      />
      {events.isLoading || prevEvents.isLoading ? (
        <Skeleton className="h-96 rounded" />
      ) : events.isError || prevEvents.isError ? (
        <div className="bg-card flex rounded border">
          <LoadError
            label="Project Activity"
            onRetry={() => {
              void events.refetch();
              void prevEvents.refetch();
            }}
          />
        </div>
      ) : idle ? (
        <Empty className="bg-card border py-8">
          <EmptyHeader>
            <EmptyTitle>No Activity in the Last 60 Days</EmptyTitle>
            <EmptyDescription>
              Events from this project show here once it starts sending them.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <OverviewCard
          dateRange={dateRange}
          events={events.data ?? {}}
          filterGroups={[]}
          id={`overview-${project.projectId}`}
          prevEvents={prevEvents.data ?? {}}
          projectId={project.projectId}
        />
      )}
    </div>
  );
}

function Home() {
  const { projects, setProjectId } = useProjects();
  const { isEnabled } = useFeatures();

  if (!projects || projects.length === 0) {
    return <Loader />;
  }

  const sorted = _.sortBy(projects, (p) => p.name);

  // Equal-width cards capped at 4 per row. The column divisor is the project
  // count (max 4), so the cards fill the full row width at small counts: 1 fills
  // the row, 2 go 50/50, 3 split into thirds, 4 into quarters, and 5+ wrap into
  // rows of 4. auto-fill (not auto-fit) keeps every card the same width — extra
  // cards on a wrapped final row stay at the column width (e.g. 4 across with 1
  // or 2 quarter-width cards left-aligned below) instead of stretching. The
  // 18.75rem (300px) floor wins on narrow screens so the grid drops to fewer
  // columns rather than shrinking cards below ~300px.
  const columns = Math.min(sorted.length, 4);
  // gap-5 = 1.25rem between columns → (columns - 1) gutters to subtract.
  const gutterRem = (columns - 1) * 1.25;

  return (
    <>
      <PageHeader
        title="Projects Dashboard"
        description="Overview of all your projects and their activity."
      />
      <PageContent>
        {isEnabled('dashboardV2') ? (
          <div className="flex flex-col gap-4">
            {sorted.map((project) => (
              <ProjectOverview
                key={project.projectId}
                onOpen={() => setProjectId(project.projectId)}
                project={project}
              />
            ))}
          </div>
        ) : (
          <div
            className="grid w-full gap-5"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(max(18.75rem, calc((100% - ${gutterRem}rem) / ${columns})), 1fr))`,
            }}
          >
            {sorted.map((p) => (
              <ProjectBox
                key={p.projectId}
                project={p}
                setProjectId={setProjectId}
              />
            ))}
          </div>
        )}
      </PageContent>
    </>
  );
}
